import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import axios from 'axios'
import { PageLayout } from '../components/common/PageLayout'
import { Section, Button } from '../components/common/DesignComponents'
import { TabNavigation, Breadcrumb } from '../components/common/NavigationComponents'
import { ProgressItem, Alert } from '../components/common/LayoutPatterns'

interface Quest {
  id: string
  name: string
  description: string
  questType: string
  objective: string
  currentProgress: number
  targetProgress: number
  rewards: Array<{
    id: string
    rewardType: string
    amount: number
    description: string
  }>
  completed: boolean
  claimed: boolean
}

interface ResetTime {
  daily: number
  weekly: number
}

export const QuestTrackerPage: React.FC = () => {
  const { childId } = useParams<{ childId: string }>()
  const [dailyQuests, setDailyQuests] = useState<Quest[]>([])
  const [weeklyQuests, setWeeklyQuests] = useState<Quest[]>([])
  const [resetTimes, setResetTimes] = useState<ResetTime>({ daily: 0, weekly: 0 })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [claimingReward, setClaimingReward] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'daily' | 'weekly'>('daily')

  useEffect(() => {
    fetchQuestData()
    const interval = setInterval(fetchQuestData, 30000) // Refresh every 30 seconds
    return () => clearInterval(interval)
  }, [childId])

  const fetchQuestData = async () => {
    try {
      setLoading(true)
      setError(null)

      const [dailyRes, weeklyRes, resetRes] = await Promise.all([
        axios.get('/api/quests/daily'),
        axios.get('/api/quests/weekly'),
        axios.get('/api/quests/reset-times'),
      ])

      setDailyQuests(dailyRes.data.data)
      setWeeklyQuests(weeklyRes.data.data)
      setResetTimes(resetRes.data.data)
    } catch (err) {
      console.error('Failed to fetch quests:', err)
      setError('Failed to load quests. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleClaimReward = async (questId: string) => {
    if (!childId) return

    try {
      setClaimingReward(questId)
      await axios.post(`/api/quests/${questId}/claim-reward`, {
        childId,
        questId,
      })

      // Refresh quests after claiming reward
      await fetchQuestData()
    } catch (err) {
      console.error('Failed to claim reward:', err)
      setError('Failed to claim reward. Try again.')
    } finally {
      setClaimingReward(null)
    }
  }

  const formatResetTime = (hours: number): string => {
    if (hours < 1) return 'Less than an hour'
    if (hours === 24) return 'Tomorrow'
    if (hours === 168) return 'Next Monday'
    return `In ${Math.round(hours)} hours`
  }

  const quests = activeTab === 'daily' ? dailyQuests : weeklyQuests
  const resetTime =
    activeTab === 'daily' ? resetTimes.daily : resetTimes.weekly

  if (loading && quests.length === 0) {
    return (
      <PageLayout title="Loading..." subtitle="">
        <div className="text-center py-12">
          <div className="text-6xl animate-bounce mb-4">📜</div>
          <p className="text-lg text-gray-600 font-bold">Loading quests...</p>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout title="Quest Tracker 🗺️" subtitle="Complete quests and earn amazing rewards!">
      <Breadcrumb items={[
        { label: '👶 Home' },
        { label: 'Quests' }
      ]} />

      {error && (
        <Alert emoji="⚠️" title="Error" message={error} type="error" />
      )}

      <TabNavigation items={[
        { 
          label: 'Daily Quests', 
          icon: '☀️', 
          active: activeTab === 'daily', 
          onClick: () => setActiveTab('daily') 
        },
        { 
          label: 'Weekly Quests', 
          icon: '📅', 
          active: activeTab === 'weekly', 
          onClick: () => setActiveTab('weekly') 
        }
      ]} />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
        <div className="text-center p-10 bg-gradient-to-br from-purple-200 to-purple-100 rounded-3xl border-4 border-purple-300 shadow-lg">
          <div className="text-6xl font-black text-purple-700 mb-2">{quests.length}</div>
          <p className="text-xl font-black text-purple-700">Total Quests</p>
          <p className="text-purple-600 font-bold">Active now</p>
        </div>
        <div className="text-center p-10 bg-gradient-to-br from-green-200 to-green-100 rounded-3xl border-4 border-green-300 shadow-lg">
          <div className="text-6xl font-black text-green-700 mb-2">{quests.filter((q) => q.completed).length}</div>
          <p className="text-xl font-black text-green-700">Completed</p>
          <p className="text-green-600 font-bold">Well done!</p>
        </div>
        <div className="text-center p-10 bg-gradient-to-br from-pink-200 to-pink-100 rounded-3xl border-4 border-pink-300 shadow-lg">
          <div className="text-6xl font-black text-pink-700 mb-2">{quests.filter((q) => q.claimed).length}</div>
          <p className="text-xl font-black text-pink-700">Rewards Claimed</p>
          <p className="text-pink-600 font-bold">Keep it up!</p>
        </div>
      </div>

      <Section title={activeTab === 'daily' ? 'Daily Quests ☀️' : 'Weekly Quests 📅'}>
        {quests.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">😴</div>
            <p className="text-lg text-gray-600 font-bold">
              No {activeTab === 'daily' ? 'daily' : 'weekly'} quests available right now
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {quests.map((quest) => {
              const progress = quest.targetProgress === 0 ? 0 : Math.min(100, (quest.currentProgress / quest.targetProgress) * 100)
              const isComplete = quest.completed || progress >= 100

              return (
                <div
                  key={quest.id}
                  className={`p-6 rounded-2xl border-3 transition transform hover:-translate-y-1 ${
                    isComplete
                      ? 'bg-gradient-to-r from-green-100 to-green-50 border-green-300 shadow-lg'
                      : 'bg-gradient-to-r from-purple-100 to-pink-50 border-purple-200 shadow-md'
                  }`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-black text-purple-700 mb-1">
                        {isComplete ? '✅' : '🎯'} {quest.name}
                      </h3>
                      <p className="text-gray-700 font-semibold">{quest.description}</p>
                    </div>
                    {isComplete && (
                      <div className="bg-green-400 text-white px-3 py-1 rounded-full text-sm font-bold">
                        DONE
                      </div>
                    )}
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-4">
                    <p className="text-sm font-bold text-purple-700 mb-2">
                      {quest.objective}: {Math.floor(quest.currentProgress)}/{quest.targetProgress}
                    </p>
                    <div className="w-full bg-purple-200 rounded-full h-4 border-2 border-purple-300">
                      <div
                        className={`h-full rounded-full transition-all ${
                          isComplete
                            ? 'bg-gradient-to-r from-green-400 to-green-300'
                            : 'bg-gradient-to-r from-pink-400 to-purple-400'
                        }`}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>

                  {/* Rewards */}
                  {quest.rewards.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
                      {quest.rewards.map((reward) => (
                        <div
                          key={reward.id}
                          className="px-4 py-2 bg-yellow-100 rounded-lg border-2 border-yellow-300"
                        >
                          <span className="text-sm font-bold text-yellow-700">
                            🎁 +{reward.amount} {reward.description}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Action Button */}
                  {isComplete && !quest.claimed ? (
                    <Button 
                      onClick={() => handleClaimReward(quest.id)}
                      variant="primary"
                      size="md"
                      className={claimingReward === quest.id ? 'opacity-50' : ''}
                    >
                      {claimingReward === quest.id ? '⏳ Claiming...' : '🎉 Claim Reward'}
                    </Button>
                  ) : quest.claimed ? (
                    <Button variant="outline" size="md" className="opacity-50">
                      ✨ Already Claimed
                    </Button>
                  ) : (
                    <Button variant="secondary" size="md" className="opacity-75">
                      ⚡ In Progress...
                    </Button>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </Section>
    </PageLayout>
  )
}

export default QuestTrackerPage
